/**
 * Google Tag Manager (GTM) Data Layer Tracking
 * 
 * Implements GTM + Data Layer specs for Signup event tracking
 * 
 * Usage:
 *   import { trackSignupComplete, captureAttributionParams } from './utils/analytics';
 * 
 *   // On page load
 *   captureAttributionParams();
 * 
 *   // After successful signup
 *   trackSignupComplete({
 *     userId: 'hashed_user_id', // Optional
 *     method: 'email', // Optional
 *     accountType: 'free_trial' // Optional
 *   });
 */

/**
 * Capture and store attribution parameters from URL
 * Run this on page load to capture UTM parameters and GCLID
 */
export const captureAttributionParams = () => {
  if (typeof window === 'undefined') {
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  const params = new URLSearchParams(window.location.search);
  const trackingParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid'];
  
  // Store UTM parameters and GCLID in localStorage (support both underscore and hyphen in URL)
  trackingParams.forEach((param) => {
    const value = params.get(param) || params.get(param.replace('_', '-'));
    if (value) {
      localStorage.setItem('nb_' + param, value);
    }
  });

  // Store landing page URL and referrer (first touch only)
  if (!sessionStorage.getItem('nb_landing_page')) {
    sessionStorage.setItem('nb_landing_page', window.location.href);
    sessionStorage.setItem('nb_referrer', document.referrer || '');
  }
};

/**
 * Get UTM attribution params for form submission.
 * Uses localStorage (captured on page load) and falls back to current URL so
 * we don't miss params (e.g. after client-side redirect).
 */
export const getAttributionParams = () => {
  if (typeof window === 'undefined') return {};
  const fromStorage = {
    utm_source: localStorage.getItem('nb_utm_source') || undefined,
    utm_medium: localStorage.getItem('nb_utm_medium') || undefined,
    utm_campaign: localStorage.getItem('nb_utm_campaign') || undefined,
    utm_content: localStorage.getItem('nb_utm_content') || undefined,
  };
  // Fallback: read from current URL (in case params weren't captured on load)
  const params = new URLSearchParams(window.location.search);
  const get = (a, b) => params.get(a) || params.get(b);
  return {
    utm_source: fromStorage.utm_source || get('utm_source', 'utm-source') || undefined,
    utm_medium: fromStorage.utm_medium || get('utm_medium', 'utm-medium') || undefined,
    utm_campaign: fromStorage.utm_campaign || get('utm_campaign', 'utm-campaign') || undefined,
    utm_content: fromStorage.utm_content || get('utm_content', 'utm-content') || undefined,
  };
};

/**
 * Track signup completion event
 * Push signup_complete event to dataLayer with attribution data
 * 
 * @param {object} signupData - Optional signup data
 * @param {string} signupData.userId - Hashed/anonymized user ID (optional)
 * @param {string} signupData.method - Signup method: 'email', 'google', 'sso', 'aws_marketplace', 'direct_website' (optional)
 * @param {string} signupData.accountType - Account type: 'free_trial', 'demo', etc. (optional)
 */
export const trackSignupComplete = (signupData = {}) => {
  if (typeof window === 'undefined') {
    console.log('DataLayer: window not available');
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  // Build event object with only available data
  const eventData = {
    event: 'signup_complete'
  };

  // OPTIONAL: Signup details - only include if available
  if (signupData && signupData.userId) {
    eventData.user_id = signupData.userId;
  }
  if (signupData && signupData.method) {
    eventData.signup_method = signupData.method;
  }
  if (signupData && signupData.accountType) {
    eventData.account_type = signupData.accountType;
  }

  // Attribution parameters (from stored values - auto-captured on page load)
  const utmSource = localStorage.getItem('nb_utm_source');
  const utmMedium = localStorage.getItem('nb_utm_medium');
  const utmCampaign = localStorage.getItem('nb_utm_campaign');
  const utmTerm = localStorage.getItem('nb_utm_term');
  const utmContent = localStorage.getItem('nb_utm_content');
  const gclid = localStorage.getItem('nb_gclid');
  const landingPage = sessionStorage.getItem('nb_landing_page');
  const referrer = sessionStorage.getItem('nb_referrer');

  // Only add attribution params if they have values (don't send empty strings)
  if (utmSource) eventData.utm_source = utmSource;
  if (utmMedium) eventData.utm_medium = utmMedium;
  if (utmCampaign) eventData.utm_campaign = utmCampaign;
  if (utmTerm) eventData.utm_term = utmTerm;
  if (utmContent) eventData.utm_content = utmContent;
  if (gclid) eventData.gclid = gclid;
  if (landingPage) eventData.landing_page = landingPage;
  if (referrer) eventData.referrer = referrer;

  // Push to dataLayer
  window.dataLayer.push(eventData);
  console.log('DataLayer event pushed:', eventData);
};

/**
 * Track registration start (optional - for debugging/analytics)
 */
export const trackRegistrationStart = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'registration_started'
  });
};

/**
 * Track registration error (optional - for debugging/analytics)
 */
export const trackRegistrationError = (errorType) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'registration_error',
    error_type: errorType || 'unknown'
  });
};
